/**
 * Automations Page
 *
 * Main page for managing automations. Displays a list of all automations
 * and allows creation, editing, cloning, and deletion.
 */

import { faCircleQuestion, faFolderPlus, faPlus } from '@fortawesome/pro-regular-svg-icons';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Formik, FormikHelpers, FormikProps, useFormikContext } from 'formik';
import { cloneDeep } from 'lodash';
import { Dispatch, ReactNode, RefObject, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import {
  ConfirmDialog,
  type UseConfirmDialogReturn,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { Icon } from 'src/components/Icon';
import { Loader } from 'src/components/Loader';
import { SearchParamSearchTextField } from 'src/components/SearchTextField';
import { SectionToolbar } from 'src/components/SectionToolbar';
import { Stack } from 'src/components/Stack';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { AutomationLayout } from 'src/models/automation-group/_types';
import {
  automationGroupsQueryKey,
  automationsQueryKey,
  buildAutomationLayout,
  createAutomationGroup,
  deleteAutomationGroup,
  fetchAutomationGroups,
  filterAutomationLayout,
  flattenAutomationLayout,
  getCreateFolderSelectableReminders,
  getEditFolderSelectableReminders,
  isAutomationLayoutReorderable,
  patchReminderInAutomationsCache,
  patchRemindersAfterGroupDelete,
  patchRemindersAfterGroupUpdate,
  patchRemindersWithMissingGroups,
  updateAutomationGroup,
} from 'src/models/automation-group/automation-group';
import { Reminder } from 'src/models/reminder/_types';
import {
  createReminder,
  deleteReminder,
  fetchReminders,
  updateReminder,
} from 'src/models/reminder/reminder';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { AutomationCard } from 'src/shared/Automation/AutomationCard';
import {
  AUTOMATION_ACTIONS,
  getAutomationAction,
  getAutomationActionLabel,
} from 'src/shared/Automation/automationActions';
import { TRIGGERS } from 'src/shared/Automation/automationTriggers';
import { AutomationItem, FilterGroup } from 'src/shared/Automation/types';
import { Filters, SelectFilter } from 'src/shared/Filters/Filters';
import { Identifier } from 'src/types/utilityTypes';
import toast from 'src/utils/toast';
import { useListSearchParams } from 'src/utils/useListSearchParams';

import { Meta } from '../../components/Meta';

import { AutomationGroupHeaderActions } from './AutomationGroupHeaderActions';
import { AutomationLayoutDnD } from './AutomationLayoutDnD';
import { AutomationGroupDialogValues, CreateAutomationGroupDialog } from './CreateAutomationGroupDialog';
import { CreateAutomationWizard } from './CreateAutomationWizard';
import { automationDragCardSx, automationPageHeaderSx } from './automationDragStyles';

interface AutomationsFormValues {
  automations: AutomationItem[];
}

const DEFAULT_AUTOMATION: Partial<AutomationItem> = {
  reminderType: '',
  delayInterval: 0,
  delayType: 'day',
  media: ['email'],
  filters: { condition: 'AND', filters: [] },
  content: [],
  tags: '',
  count: 1,
  trainerId: 0,
  sendToIds: [],
  reportFilters: [],
  automationGroupId: null,
  position: 0,
  enabled: true,
};

const AUTOMATION_TYPE_DEFAULTS: Record<string, Partial<AutomationItem>> = {
  custom: {
    delayInterval: 1,
    delayType: 'day',
    media: ['email'],
    reminderType: 'workout',
  },
  'close-out-visit': {
    content: [],
    delayInterval: 1,
    delayType: 'day',
  },
  'workout-today': {
    delayInterval: 8,
    delayType: 'hour',
    media: ['email'],
  },
  'client-tag': {
    reminderType: 'assessment',
    delayInterval: 0,
    delayType: 'hour',
  },
  message: {
    reminderType: 'client-signup',
    media: ['message'],
    delayType: 'day',
    delayInterval: 0,
  },
  'upcoming-appointment': {
    reminderType: 'upcoming-appointment',
    delayType: 'hour',
    delayInterval: 48,
  },
  'visits-left': {
    reminderType: 'visits-left',
    count: 1,
    media: ['email'],
  },
  'cancel-user': {
    delayInterval: 0,
    delayType: 'day',
    reminderType: 'cancel_subscription',
  },
  'clear-modal-alert': {
    delayInterval: 0,
    delayType: 'day',
    reminderType: 'successful-payment',
  },
};

function normalizeAutomationFilters(filters: unknown): FilterGroup {
  if (
    filters &&
    typeof filters === 'object' &&
    !Array.isArray(filters) &&
    'condition' in filters &&
    Array.isArray((filters as FilterGroup).filters)
  ) {
    return filters as FilterGroup;
  }

  return { condition: 'AND', filters: [] };
}

function normalizeAutomations(data: Reminder[]): AutomationItem[] {
  return data.map((r) => ({
    ...r,
    automationGroupId: r.automationGroupId ?? null,
    position: r.position ?? 0,
    content: Array.isArray(r.content) ? r.content : (r.content ? [r.content] : []),
    filters: normalizeAutomationFilters(r.filters),
  }));
}

const findAutomationIndex = (automations: AutomationItem[], reminderId?: Identifier) =>
  automations.findIndex(
    (automation) =>
      reminderId !== undefined &&
      automation.id !== undefined &&
      String(automation.id) === String(reminderId)
  );

function AutomationsFormContent({
  automationLayout,
  collapseAllRef,
  confirm,
  customTypeFilter,
  deleteMutation,
  folderFilter,
  folderDialogReminders,
  formRef,
  fullAutomationLayout,
  onAllGroupsCollapsedChange,
  onDeleteGroup,
  onUpdateGroup,
  reorderEnabled,
  reminders,
  resetKey,
  setResetKey,
  unsavedSectionRef,
}: {
  automationLayout: AutomationLayout;
  collapseAllRef: RefObject<(() => void) | null>;
  confirm: UseConfirmDialogReturn[1];
  customTypeFilter: string | undefined;
  deleteMutation: {
    mutateAsync: (id: number) => Promise<unknown>;
  };
  folderFilter: string | undefined;
  folderDialogReminders: Reminder[];
  formRef: RefObject<FormikProps<AutomationsFormValues> | null>;
  fullAutomationLayout: AutomationLayout;
  onAllGroupsCollapsedChange: (allCollapsed: boolean) => void;
  onDeleteGroup: (groupId: Identifier) => Promise<void>;
  onUpdateGroup: (groupId: Identifier, values: AutomationGroupDialogValues) => Promise<void>;
  reorderEnabled: boolean;
  reminders: Reminder[];
  resetKey: number;
  setResetKey: Dispatch<SetStateAction<number>>;
  unsavedSectionRef: RefObject<HTMLDivElement | null>;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [savingAutomationId, setSavingAutomationId] = useState<Identifier | null>(null);
  const [editingGroupId, setEditingGroupId] = useState<Identifier | null>(null);
  const { values, setFieldValue, setValues } =
    useFormikContext<AutomationsFormValues>();
  const automationsRef = useRef(values.automations);
  automationsRef.current = values.automations;
  const [togglingIndex, setTogglingIndex] = useState<number | null>(null);

  const handleToggleEnabled = async (index: number, nextEnabled: boolean) => {
    const automation = values.automations[index];
    if (!automation.id) {
      return;
    }

    const previousEnabled = automation.enabled ?? true;
    setTogglingIndex(index);
    setFieldValue(`automations.${index}.enabled`, nextEnabled);

    try {
      await updateReminder({ id: automation.id, enabled: nextEnabled });
      await queryClient.invalidateQueries({
        queryKey: ['automations', customTypeFilter],
      });
      toast.success(
        nextEnabled
          ? t('automations.common.toggleEnabledSuccess')
          : t('automations.common.toggleDisabledSuccess')
      );
    } catch {
      setFieldValue(`automations.${index}.enabled`, previousEnabled);
      toast.error(t('automations.common.toggleEnabledError'));
    } finally {
      setTogglingIndex(null);
    }
  };

  const unsavedAutomations = values.automations.filter((automation) => !automation.id);

  useEffect(() => {
    if (values.automations.length === 0 && reminders.length > 0) {
      setValues({
        automations: normalizeAutomations(reminders),
      });
    }
  }, [reminders, setValues, values.automations.length]);

  const refreshAutomations = async () => {
    await queryClient.refetchQueries({
      queryKey: automationsQueryKey(customTypeFilter),
    });
    const freshReminders =
      queryClient.getQueryData<Reminder[]>(automationsQueryKey(customTypeFilter)) ??
      reminders;
    setFieldValue('automations', [
      ...unsavedAutomations,
      ...normalizeAutomations(freshReminders),
    ]);
  };

  const handleRemove = async (_index: number, automation: AutomationItem) => {
    if (!automation.id) {
      return;
    }
    const confirmed = await confirm({
      contentAlignment: 'left',
      message: t('automations.page.deleteConfirm'),
      title: t('automations.page.deleteTitle'),
      titleAlignment: 'left',
    });
    if (!confirmed) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(automation.id);
      await refreshAutomations();
      setResetKey((k) => k + 1);
    } catch {
      // handled by mutation
    }
  };

  const getFormAutomations = () =>
    formRef.current?.values.automations ?? automationsRef.current;

  const updateAutomationById = (
    automationId: Identifier,
    patch: Partial<AutomationItem>
  ) => {
    setFieldValue(
      'automations',
      getFormAutomations().map((automation) =>
        automation.id != null && String(automation.id) === String(automationId)
          ? { ...automation, ...patch }
          : automation
      )
    );
  };

  const findAutomationById = (automationId: Identifier | undefined) => {
    if (automationId == null) {
      return undefined;
    }

    return getFormAutomations().find(
      (automation) =>
        automation.id != null && String(automation.id) === String(automationId)
    );
  };

  const handleSaveCard = async (
    automationId: Identifier | undefined,
    index: number
  ) => {
    const automation =
      findAutomationById(automationId) ?? getFormAutomations()[index];
    if (!automation) {
      toast.error(t('automations.page.failedToUpdate'));
      return;
    }

    const reminderData = automation as unknown as Reminder;
    setSavingAutomationId(automation.id ?? `unsaved-${index}`);
    try {
      let savedReminder: Reminder;
      if (automation.id) {
        savedReminder = await updateReminder(reminderData);
        patchReminderInAutomationsCache(queryClient, savedReminder);
      } else {
        savedReminder = await createReminder(reminderData);
        patchReminderInAutomationsCache(queryClient, savedReminder);
      }

      const normalizedSaved = normalizeAutomations([savedReminder])[0];
      const nextAutomations = getFormAutomations().map((value) => {
        if (
          value === automation ||
          (savedReminder.id != null &&
            value.id != null &&
            String(value.id) === String(savedReminder.id))
        ) {
          return normalizedSaved;
        }
        return value;
      });
      setValues({ automations: nextAutomations });
      setResetKey((k) => k + 1);
      toast.success(
        savedReminder.name
          ? t('automations.page.updatedSuccessfullyNamed', { name: savedReminder.name })
          : t('automations.page.updatedSuccessfully')
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t('automations.page.failedToUpdate')
      );
    } finally {
      setSavingAutomationId(null);
    }
  };

  const handleDiscardCard = (index: number) => {
    const automation = values.automations[index];
    if (!automation.id) {
      setFieldValue(
        'automations',
        values.automations.filter((_, currentIndex) => currentIndex !== index)
      );
    } else {
      const fromServer = normalizeAutomations(reminders).find(
        (item) =>
          item.id != null && String(item.id) === String(automation.id)
      );
      if (fromServer) {
        updateAutomationById(automation.id!, cloneDeep(fromServer));
      }
    }
    setResetKey((k) => k + 1);
  };

  const handleClone = (index: number, automation: AutomationItem) => {
    const cloned = {
      ...automation,
      id: undefined,
      automationGroupId: automation.automationGroupId ?? null,
      name: `${automation.name || t('automations.common.automation')} (${t('automations.page.copy')})`,
      enabled: true,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    } as AutomationItem;
    setFieldValue('automations', [cloned, ...values.automations]);
  };

  const handleReordered = (updatedLayout: AutomationLayout) => {
    queryClient.setQueryData(
      automationsQueryKey(customTypeFilter),
      flattenAutomationLayout(updatedLayout)
    );
    queryClient.setQueryData(
      automationGroupsQueryKey(),
      updatedLayout.automationGroups.map(({ reminders: _reminders, ...group }) => group)
    );

    const flattened = normalizeAutomations(flattenAutomationLayout(updatedLayout));
    const automationsById = new Map(flattened.map((automation) => [automation.id, automation]));

    setFieldValue(
      'automations',
      values.automations.map((automation) => {
        if (!automation.id || !automationsById.has(automation.id)) {
          return automation;
        }

        const updatedAutomation = automationsById.get(automation.id)!;
        return {
          ...automation,
          automationGroupId: updatedAutomation.automationGroupId,
          position: updatedAutomation.position,
        };
      })
    );
  };

  const handleReorderFailed = () => {
    toast.error(t('automations.groups.failedToReorder'));
  };

  const renderAutomationCard = (
    automation: AutomationItem,
    index: number,
    dragHandle?: ReactNode,
    compactCard = false,
    dragging = false
  ) => {
    const actionConfig = getAutomationAction(automation.customType);
    if (!actionConfig) {
      return (
        <Card>
          <Box p={2}>
            <Typography>
              {t('automations.page.unknownActionType', { type: automation.customType })}
            </Typography>
          </Box>
        </Card>
      );
    }

    const {
      WhatSection,
      WhatSummary,
      WhenSection,
      WhenSummary,
      showMedia,
      showFilters,
      showMessage,
      showSendTo,
      sendToPlaceholder,
    } = actionConfig;

    return (
      <AutomationCard
        automation={automation}
        cardSx={dragging ? automationDragCardSx : undefined}
        compactCard={compactCard}
        dragHandle={dragHandle}
        flatCard={Boolean(dragHandle) || compactCard || dragging}
        index={index}
        isTogglingEnabled={togglingIndex === index}
        isSavingCard={
          automation.id != null
            ? savingAutomationId === automation.id
            : savingAutomationId === `unsaved-${index}`
        }
        onClone={() => handleClone(index, automation)}
        onDiscardCard={() => handleDiscardCard(index)}
        onToggleEnabled={(enabled) => handleToggleEnabled(index, enabled)}
        onRemove={() => {
          if (automation.id) {
            handleRemove(index, automation);
          } else {
            setFieldValue(
              'automations',
              values.automations.filter((_, currentIndex) => currentIndex !== index)
            );
          }
        }}
        onSaveCard={() => {
          void handleSaveCard(automation.id, index);
        }}
        onUpdateAutomation={
          automation.id
            ? (patch) => updateAutomationById(automation.id!, patch)
            : undefined
        }
        renderWhatSection={() => <WhatSection automation={automation} index={index} />}
        renderWhatSummary={() => <WhatSummary automation={automation} />}
        renderWhenSection={
          WhenSection ? () => <WhenSection automation={automation} index={index} /> : undefined
        }
        renderWhenSummaryOverride={
          WhenSummary ? () => <WhenSummary automation={automation} /> : undefined
        }
        resetKey={resetKey}
        sendToPlaceholder={sendToPlaceholder}
        showFiltersSection={showFilters}
        showMediaSection={showMedia}
        showMessageSection={showMessage}
        showSendToSection={showSendTo}
      />
    );
  };

  const renderReminderById = (
    reminderId: Identifier,
    dragHandle: ReactNode,
    options?: { dragging?: boolean; grouped?: boolean }
  ) => {
    const index = findAutomationIndex(values.automations, reminderId);
    if (index === -1) {
      return null;
    }
    const automation = values.automations[index];

    return renderAutomationCard(
      automation,
      index,
      dragHandle,
      options?.grouped,
      options?.dragging
    );
  };

  const renderGroupHeader = (
    group: AutomationLayout['automationGroups'][number],
    { collapsed, onToggleCollapse }: { collapsed: boolean; onToggleCollapse: () => void }
  ) => (
    <Box alignItems="center" display="flex" justifyContent="space-between" width="100%">
      <Typography
        color="text.primary"
        sx={{ m: 0 }}
        variant="h6"
      >
        {group.name}
      </Typography>
      <AutomationGroupHeaderActions
        collapsed={collapsed}
        onRename={() => setEditingGroupId(group.id)}
        onToggleCollapse={onToggleCollapse}
      />
    </Box>
  );

  const hasSavedAutomations =
    reminders.length > 0 ||
    automationLayout.automationGroups.length > 0 ||
    unsavedAutomations.length > 0;

  if (!hasSavedAutomations) {
    return (
      <Card>
        <Box p={3} textAlign="center">
          <Typography color="text.secondary" variant="body1">
            {t('automations.page.noAutomationsFound')}
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Stack spacing={3}>
      {editingGroupId ? (() => {
        const editingGroup = fullAutomationLayout.automationGroups.find(
          (group) => String(group.id) === String(editingGroupId)
        );
        if (!editingGroup) {
          return null;
        }

        const selectableReminders = getEditFolderSelectableReminders(
          fullAutomationLayout.automationGroups,
          folderDialogReminders,
          editingGroupId,
          values.automations
        );
        const groupReminderIds = selectableReminders
          .filter(
            (reminder) =>
              reminder.automationGroupId != null &&
              String(reminder.automationGroupId) === String(editingGroupId)
          )
          .map((reminder) => reminder.id!)
          .filter(Boolean);

        return (
          <CreateAutomationGroupDialog
            key={String(editingGroupId)}
            initialValues={{
              description: editingGroup.description ?? '',
              name: editingGroup.name,
              reminderIds: groupReminderIds,
            }}
            onClose={() => setEditingGroupId(null)}
            onDelete={async () => {
              await onDeleteGroup(editingGroupId);
              setEditingGroupId(null);
            }}
            onSave={async (values) => {
              await onUpdateGroup(editingGroupId, values);
              setEditingGroupId(null);
            }}
            open
            selectableReminders={selectableReminders}
            variant="edit"
          />
        );
      })() : null}

      {unsavedAutomations.length > 0 ? (
        <Box ref={unsavedSectionRef}>
          <Typography fontWeight="bold" mb={2} variant="body1">
            {t('automations.groups.unsavedAutomations')}
          </Typography>
          <Stack spacing={2}>
            {unsavedAutomations.map((automation) => {
              const index = values.automations.findIndex((item) => item === automation);
              return (
                <Box key={`unsaved-${index}`}>
                  {renderAutomationCard(automation, index)}
                </Box>
              );
            })}
          </Stack>
        </Box>
      ) : null}

      <AutomationLayoutDnD
        collapseAllRef={collapseAllRef}
        dragDisabled={!reorderEnabled}
        layout={automationLayout}
        onAllGroupsCollapsedChange={onAllGroupsCollapsedChange}
        onReorderFailed={handleReorderFailed}
        onReordered={handleReordered}
        renderGroupHeader={renderGroupHeader}
        renderReminder={renderReminderById}
        showUngroupedSection={!folderFilter || folderFilter === 'ungrouped'}
        ungroupedFilterActive={folderFilter === 'ungrouped'}
      />
    </Stack>
  );
}

export default function AutomationsPage() {
  const { t } = useTranslation();
  const { session } = useSession();
  const queryClient = useQueryClient();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [deleteConfirmProps, confirm] = useConfirmDialogComponent();
  const formRef = useRef<FormikProps<AutomationsFormValues>>(null);
  const [resetKey, setResetKey] = useState(0);
  const collapseAllRef = useRef<(() => void) | null>(null);
  const unsavedSectionRef = useRef<HTMLDivElement | null>(null);
  const [allGroupsCollapsed, setAllGroupsCollapsed] = useState(false);

  const {
    params,
    updateParam,
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
  } = useListSearchParams({
    defaultPerPage: 100,
  });

  const customTypeFilter = getFilterParamValue('customType') as string | undefined;
  const rawFolderFilter = getFilterParamValue('folderId');
  const folderFilter =
    rawFolderFilter != null && rawFolderFilter !== ''
      ? String(rawFolderFilter)
      : undefined;
  const enabledStatusParam = getFilterParamValue('enabledStatus');
  const enabledStatus =
    enabledStatusParam === 'active' || enabledStatusParam === 'disabled'
      ? enabledStatusParam
      : undefined;

  const automationTypeOptions = Object.entries(AUTOMATION_ACTIONS)
    .map(([key]) => ({
      value: key,
      label: getAutomationActionLabel(key, t),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const { data: reminders = [], isLoading: remindersLoading } = useQuery({
    queryKey: automationsQueryKey(customTypeFilter),
    queryFn: () =>
      fetchReminders({
        fetchAll: true,
        userId: session?.trainerId,
        customType: customTypeFilter || undefined,
      }),
    enabled: Boolean(session?.trainerId),
  });

  const { data: allReminders = [] } = useQuery({
    queryKey: automationsQueryKey(),
    queryFn: () =>
      fetchReminders({
        fetchAll: true,
        userId: session?.trainerId,
      }),
    enabled: Boolean(session?.trainerId),
  });

  const folderDialogReminders = customTypeFilter ? allReminders : reminders;

  const { data: automationGroups = [], isLoading: groupsLoading } = useQuery({
    queryKey: automationGroupsQueryKey(),
    queryFn: fetchAutomationGroups,
  });

  const isLoading = remindersLoading || groupsLoading;

  const layout = useMemo(
    () => buildAutomationLayout(automationGroups, reminders),
    [automationGroups, reminders]
  );

  const filteredLayout = useMemo(
    () =>
      filterAutomationLayout(layout, {
        folderId: folderFilter || undefined,
        enabledStatus,
        search: params.search,
      }),
    [enabledStatus, folderFilter, layout, params.search]
  );

  const reorderEnabled = useMemo(() => {
    if (customTypeFilter) {
      return false;
    }

    if (params.search?.trim()) {
      return false;
    }

    return isAutomationLayoutReorderable(filteredLayout, layout, {
      includeUngrouped: !folderFilter || folderFilter === 'ungrouped',
    });
  }, [customTypeFilter, filteredLayout, folderFilter, layout, params.search]);

  const folderFilterOptions = useMemo(
    () => [
      { value: 'ungrouped', label: t('automations.groups.ungrouped') },
      ...automationGroups.map((group) => ({
        value: String(group.id),
        label: group.name,
      })),
    ],
    [automationGroups, t]
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }

    patchRemindersWithMissingGroups(queryClient, automationGroups);
  }, [automationGroups, isLoading, queryClient]);

  const invalidateAutomationQueries = async () => {
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ['automations'] }),
      queryClient.refetchQueries({ queryKey: automationGroupsQueryKey() }),
    ]);
  };

  const syncFormAutomationsFromQuery = () => {
    const freshReminders =
      queryClient.getQueryData<Reminder[]>(automationsQueryKey(customTypeFilter)) ??
      reminders;
    formRef.current?.setFieldValue(
      'automations',
      normalizeAutomations(freshReminders)
    );
  };

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteReminder(id),
    onSuccess: () => {
      void invalidateAutomationQueries();
      toast.success(t('automations.page.deletedSuccessfully'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('automations.page.failedToDelete'));
    },
  });

  const createGroupMutation = useMutation({
    mutationFn: createAutomationGroup,
    onSuccess: () => {
      void invalidateAutomationQueries();
      toast.success(t('automations.groups.createdSuccessfully'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('automations.groups.failedToCreate'));
    },
  });

  const handleDeleteGroup = async (groupId: Identifier) => {
    await deleteAutomationGroup(groupId);
    patchRemindersAfterGroupDelete(queryClient, groupId);
    syncFormAutomationsFromQuery();
    toast.success(t('automations.groups.deletedSuccessfully'));
  };

  const handleUpdateGroup = async (
    groupId: Identifier,
    values: AutomationGroupDialogValues
  ) => {
    await updateAutomationGroup(groupId, {
      description: values.description,
      name: values.name,
      reminderIds: values.reminderIds,
    });
    patchRemindersAfterGroupUpdate(queryClient, groupId, values.reminderIds);
    await invalidateAutomationQueries();
    syncFormAutomationsFromQuery();
    toast.success(t('automations.groups.updatedSuccessfully'));
  };

  const handleSubmit = async (
    values: AutomationsFormValues,
    helpers: FormikHelpers<AutomationsFormValues>
  ) => {
    const saveResults = await Promise.allSettled(
      values.automations.map(async (automation) => {
        const reminderData = automation as unknown as Reminder;
        if (automation.id) {
          const savedReminder = await updateReminder(reminderData);
          patchReminderInAutomationsCache(queryClient, savedReminder);
          return savedReminder;
        }

        return createReminder(reminderData);
      })
    );

    const failures = saveResults.filter((result) => result.status === 'rejected');

    try {
      if (failures.length > 0) {
        toast.error(t('automations.page.saveAllError'));
        return;
      }

      await queryClient.refetchQueries({
        queryKey: automationsQueryKey(customTypeFilter),
      });
      const freshReminders =
        queryClient.getQueryData<Reminder[]>(automationsQueryKey(customTypeFilter)) ??
        reminders;
      helpers.resetForm({
        values: { automations: normalizeAutomations(freshReminders) },
      });
      setResetKey((k) => k + 1);
      toast.success(
        t('automations.page.saveAllSuccess', {
          count: saveResults.length,
        })
      );
    } finally {
      helpers.setSubmitting(false);
    }
  };

  const filterConfigs = [
    {
      field: 'customType',
      label: t('automations.page.automationType'),
      Component: SelectFilter,
      props: {
        defaultOptionLabel: t('automations.page.statusAll'),
        options: automationTypeOptions,
        searchable: true,
      },
    },
    {
      field: 'folderId',
      label: t('automations.page.automationFolder'),
      Component: SelectFilter,
      props: {
        defaultOptionLabel: t('automations.page.statusAll'),
        options: folderFilterOptions,
        searchable: true,
      },
    },
    {
      field: 'enabledStatus',
      label: t('automations.page.automationStatus'),
      Component: SelectFilter,
      props: {
        defaultOptionLabel: t('automations.page.statusAll'),
        options: [
          { value: 'active', label: t('automations.page.statusActive') },
          { value: 'disabled', label: t('automations.page.statusDisabled') },
        ],
      },
    },
  ];

  return (
    <>
      <Meta brand={session?.brand} title={t('automations.page.title')} />
      <AppFrameConfig
        actions={
          <Stack alignItems="center" direction="row" spacing={2}>
            <Button
              disabled={isLoading}
              onClick={() => {
                void formRef.current?.submitForm();
              }}
              variant="bordered"
            >
              {t('automations.page.saveAll')}
            </Button>
            <Button
              endIcon={
                <Tooltip title={t('automations.page.foldersInfoTooltip')}>
                  <Box
                    alignItems="center"
                    component="span"
                    display="inline-flex"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Icon icon={faCircleQuestion} size="small" />
                  </Box>
                </Tooltip>
              }
              onClick={() => setGroupDialogOpen(true)}
              startIcon={<Icon icon={faFolderPlus} />}
              variant="bordered"
            >
              {t('automations.groups.createGroup')}
            </Button>
            <Button
              onClick={() => setWizardOpen(true)}
              startIcon={<Icon icon={faPlus} />}
              variant="prominent"
            >
              {t('automations.page.createNew')}
            </Button>
          </Stack>
        }
        containerMaxWidth="lg"
        fixedHeader
        id="automations"
        pageHeaderSx={automationPageHeaderSx}
        row={
          <Box alignItems="center" display="flex" flexWrap="wrap" gap={1}>
            <Typography color="text.secondary" variant="body2">
              {t(
                reorderEnabled
                  ? 'automations.page.layoutHelp'
                  : 'automations.page.layoutHelpReorderDisabled'
              )}
            </Typography>
            <Box flex={1} />
            {automationGroups.length > 0 ? (
              <Button
                onClick={() => collapseAllRef.current?.()}
                size="small"
                variant="borderless"
              >
                {t(
                  allGroupsCollapsed
                    ? 'automations.page.expandAll'
                    : 'automations.page.collapseAll'
                )}
              </Button>
            ) : null}
          </Box>
        }
        title={t('automations.page.title')}
      />

      <SectionToolbar sx={{ alignItems: 'center', pb: 3, pt: 0, px: 0 }}>
        <Filters
          filters={filterConfigs}
          getFilterParamValue={getFilterParamValue}
          hideMainDropdown
          params={params}
          resetFilterParams={resetFilterParams}
          updateFilterParam={updateFilterParam}
          updateParam={updateParam}
        />
        <Box flex={1} />
        <SearchParamSearchTextField
          params={params}
          placeholder={t('automations.page.searchPlaceholder')}
          size="small"
          sx={{ width: { xs: '100%', md: '320px' } }}
          updateParam={updateParam}
        />
      </SectionToolbar>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <Loader size="large" />
        </Box>
      ) : (
        <Formik
          enableReinitialize={false}
          initialValues={{
            automations: normalizeAutomations(reminders),
          }}
          innerRef={formRef}
          key={String(customTypeFilter ?? '')}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit: formikSubmit, setFieldValue, values }) => {
            const handleWizardAdd = (data: {
              automationGroupId?: Identifier | null;
              customType: string;
              reminderType: string;
              name: string;
            }) => {
              const typeDefaults = AUTOMATION_TYPE_DEFAULTS[data.customType] || {};
              const reminderType =
                data.reminderType || typeDefaults.reminderType || 'all';
              const triggerConfig = TRIGGERS[reminderType];
              const automation = {
                ...DEFAULT_AUTOMATION,
                ...typeDefaults,
                automationGroupId: data.automationGroupId ?? null,
                customType: data.customType,
                reminderType,
                name: data.name,
                trainerId: session?.trainerId || 0,
                createdAt: Date.now(),
                updatedAt: Date.now(),
              } as AutomationItem;

              if (triggerConfig?.delayTypes?.length) {
                automation.delayType = triggerConfig.delayTypes[0];
              }
              if (data.customType === 'client-tag' && !automation.tags) {
                automation.tags = 'add';
              }

              setFieldValue('automations', [automation, ...values.automations]);
              setWizardOpen(false);
              requestAnimationFrame(() => {
                unsavedSectionRef.current?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                });
              });
            };

            return (
              <>
                <form onSubmit={formikSubmit}>
                  <AutomationsFormContent
                    automationLayout={filteredLayout}
                    collapseAllRef={collapseAllRef}
                    confirm={confirm}
                    customTypeFilter={customTypeFilter}
                    deleteMutation={deleteMutation}
                    folderDialogReminders={folderDialogReminders}
                    folderFilter={folderFilter}
                    formRef={formRef}
                    fullAutomationLayout={layout}
                    onAllGroupsCollapsedChange={setAllGroupsCollapsed}
                    onDeleteGroup={handleDeleteGroup}
                    onUpdateGroup={handleUpdateGroup}
                    reorderEnabled={reorderEnabled}
                    reminders={reminders}
                    resetKey={resetKey}
                    setResetKey={setResetKey}
                    unsavedSectionRef={unsavedSectionRef}
                  />
                </form>
                <CreateAutomationWizard
                  folders={automationGroups}
                  onAdd={handleWizardAdd}
                  onClose={() => setWizardOpen(false)}
                  open={wizardOpen}
                />
                <CreateAutomationGroupDialog
                  onClose={() => setGroupDialogOpen(false)}
                  onSave={async (values) => {
                    await createGroupMutation.mutateAsync(values);
                    setGroupDialogOpen(false);
                  }}
                  open={groupDialogOpen}
                  selectableReminders={getCreateFolderSelectableReminders(
                    automationGroups,
                    folderDialogReminders,
                    values.automations
                  )}
                  variant="create"
                />
              </>
            );
          }}
        </Formik>
      )}

      <ConfirmDialog {...deleteConfirmProps} />
    </>
  );
}
